import { useIntl } from "react-intl";
import { DataTableImageColumn } from "~components/DataTable/components/DataTableImageColumn/DataTableImageColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { LeagueTableColumns } from "~pages/Leagues/LeagueListPage/enums/leagueTableColumns.ts";
import type { Image } from "~types/image.ts";
import type { League } from "~types/league.ts";
import { sortStringValues } from "~utils/utilities.ts";

export const useGetLeagueTableColumns = (): Array<DataTableColumn<League>> => {
  const { formatMessage } = useIntl();

  return [
    {
      key: LeagueTableColumns.Logo,
      title: formatMessage({ id: "leagueListPage.table.column.logo" }),
      dataIndex: "images",
      width: 60,
      render: (value: Array<Image>) => {
        return <DataTableImageColumn images={value} imageTypes={[ImageTypes.Logo]} />;
      },
    },
    {
      key: LeagueTableColumns.NameLocal,
      title: formatMessage({ id: "leagueListPage.table.column.name.local.full" }),
      dataIndex: "nameLocal",
      ellipsis: { showTitle: true },
      sorter: (a: League, b: League) => sortStringValues(a.nameLocal ?? "", b.nameLocal ?? ""),
    },
    {
      key: LeagueTableColumns.NameLatin,
      title: formatMessage({ id: "leagueListPage.table.column.name.latin.full" }),
      dataIndex: "nameLatin",
      sorter: (a: League, b: League) => sortStringValues(a.nameLatin ?? "", b.nameLatin ?? ""),
    },
    {
      title: formatMessage({ id: "leagueListPage.table.column.abbreviationLocal" }),
      dataIndex: "abbreviationLocal",
      key: LeagueTableColumns.AbbreviationLocal,
      sorter: (a, b) => sortStringValues(a.abbreviationLocal ?? "", b.abbreviationLocal ?? ""),
      width: 100,
      align: "center",
    },
    {
      title: formatMessage({ id: "leagueListPage.table.column.abbreviationLatin" }),
      dataIndex: "abbreviationLatin",
      key: LeagueTableColumns.AbbreviationLatin,
      sorter: (a, b) => sortStringValues(a.abbreviationLatin ?? "", b.abbreviationLatin ?? ""),
      width: 100,
      align: "center",
    },
    {
      key: LeagueTableColumns.RegionType,
      title: formatMessage({ id: "leagueListPage.table.column.regionType" }),
      dataIndex: "regionType",
      width: 160,
      sorter: (a: League, b: League) => sortStringValues(a.regionType ?? "", b.regionType ?? ""),
    },
    {
      key: LeagueTableColumns.CountryCode,
      title: formatMessage({ id: "country" }),
      dataIndex: "countryCode",
      width: 140,
      render: (value: string | null | undefined) => (value ? formatMessage({ id: `countryCode.${value}` }) : ""),
    },
    {
      key: LeagueTableColumns.ExternalId,
      title: formatMessage({ id: "leagueListPage.table.column.externalId" }),
      dataIndex: "externalId",
      copyable: true,
      width: 220,
    },
    {
      key: LeagueTableColumns.LeagueId,
      title: formatMessage({ id: "leagueListPage.table.column.league.id" }),
      dataIndex: "leagueId",
      copyable: true,
      width: 220,
    },
  ];
};
